import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Calendar, Download, Eye, FileText, Mail, Send, X } from "lucide-react";
import Swal from "sweetalert2";
import { API_BASE_URL } from "../../../../../api";
import "../TraitementStyles/ExportArretePaie.css";

const ExportArretePaie = () => {
    const [recipientInput, setRecipientInput] = useState("");
    const [recipients, setRecipients] = useState([]);
    const [fileDate] = useState(new Date().toISOString().slice(0, 10));
    const [payDay, setPayDay] = useState("25");
    const [autoSend, setAutoSend] = useState(false);
    const [emailBody, setEmailBody] = useState("Bonjour,\n\nVeuillez trouver ci-joint le fichier des arrêtés de paie.\n\nCordialement,");
    const [previewUrl, setPreviewUrl] = useState("");
    const [generating, setGenerating] = useState(false);
    const [sending, setSending] = useState(false);

    useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/export-arrete/config`, {
                    headers: { Authorization: sessionStorage.getItem("token") ? `Bearer ${sessionStorage.getItem("token")}` : undefined, uid: sessionStorage.getItem("uid") }
                });
                if (response.data) {
                    const config = response.data;
                    setPayDay(String(config.payDay || "25"));
                    setAutoSend(config.autoSend || false);
                    if (config.recipients) {
                        setRecipients(config.recipients.split(",").map(r => r.trim()).filter(Boolean));
                    } else {
                        setRecipients([]);
                    }
                    setEmailBody(config.emailBody || "Bonjour,\n\nVeuillez trouver ci-joint le fichier des arrêtés de paie.\n\nCordialement,");
                }
            } catch (error) {
                console.error("Erreur lors de la récupération de la configuration", error);
            }
        };
        fetchConfig();
    }, []);

    const addRecipients = () => {
        const emails = recipientInput.split(/[;,\s]+/).map(value => value.trim()).filter(Boolean);
        const invalid = emails.find(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
        if (invalid) return Swal.fire({ icon: "warning", title: "Adresse invalide", text: `${invalid} n'est pas une adresse email valide.` });
        setRecipients(prev => [...new Set([...prev, ...emails])]); setRecipientInput("");
    };
    const saveConfiguration = async () => {
        const typedEmails = recipientInput.split(/[;,\s]+/).map(value => value.trim()).filter(Boolean);
        const invalid = typedEmails.find(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
        if (invalid) return Swal.fire({ icon: "warning", title: "Adresse invalide", text: `${invalid} n'est pas une adresse email valide.` });
        const savedRecipients = [...new Set([...recipients, ...typedEmails])];
        if (!savedRecipients.length) return Swal.fire({ icon: "warning", title: "Destinataire requis", text: "Ajoutez au moins une adresse email avant d'enregistrer." });
        setRecipients(savedRecipients);
        setRecipientInput("");
        try {
            await axios.post(`${API_BASE_URL}/api/export-arrete/config`, {
                payDay: Number(payDay),
                autoSend,
                recipients: savedRecipients.join(","),
                emailBody,
                employeeIds: ""
            }, {
                headers: { Authorization: sessionStorage.getItem("token") ? `Bearer ${sessionStorage.getItem("token")}` : undefined, uid: sessionStorage.getItem("uid") }
            });
            Swal.fire({ icon: "success", title: "Informations enregistrées", text: "La configuration a été enregistrée avec succès.", timer: 1800, showConfirmButton: false });
        } catch {
            Swal.fire({ icon: "error", title: "Erreur", text: "Impossible d'enregistrer la configuration." });
        }
    };
    const payPeriod = useMemo(() => {
        const reference = new Date(`${fileDate}T00:00:00`);
        const day = Math.min(Math.max(Number(payDay) || 1, 1), 31);
        const end = new Date(reference.getFullYear(), reference.getMonth(), Math.min(day, new Date(reference.getFullYear(), reference.getMonth() + 1, 0).getDate()));
        const start = new Date(reference.getFullYear(), reference.getMonth() - 1, Math.min(day, new Date(reference.getFullYear(), reference.getMonth(), 0).getDate()) + 1);
        return `${start.toLocaleDateString("fr-FR")} au ${end.toLocaleDateString("fr-FR")}`;
    }, [fileDate, payDay]);
    const generate = async () => {
        setGenerating(true);
        try {
            const response = await axios.post(`${API_BASE_URL}/api/export-arrete`, { fileDate, payDay: Number(payDay), employeeIds: [], recipients, emailBody }, {
                responseType: "blob", headers: { Authorization: sessionStorage.getItem("token") ? `Bearer ${sessionStorage.getItem("token")}` : undefined, uid: sessionStorage.getItem("uid") }
            });
            setPreviewUrl(URL.createObjectURL(response.data));
        } catch { Swal.fire({ icon: "error", title: "Génération impossible", text: "Le fichier n'a pas pu être généré." }); }
        finally { setGenerating(false); }
    };
    const download = () => { const link = document.createElement("a"); link.href = previewUrl; link.download = `arrete_${fileDate}.xlsx`; link.click(); };
    const sendEmail = async () => {
        if (!recipients.length) return Swal.fire({ icon: "warning", title: "Destinataire requis", text: "Ajoutez au moins une adresse email." });
        setSending(true);
        try {
            await axios.post(`${API_BASE_URL}/api/export-arrete/send`, { fileDate, payDay: Number(payDay), employeeIds: [], recipients, emailBody }, {
                headers: { Authorization: sessionStorage.getItem("token") ? `Bearer ${sessionStorage.getItem("token")}` : undefined, uid: sessionStorage.getItem("uid") }
            });
            Swal.fire({ icon: "success", title: "Email envoyé", text: `L'arrêté a été envoyé à ${recipients.length} destinataire(s).` });
        } catch { Swal.fire({ icon: "error", title: "Envoi impossible", text: "L'email n'a pas pu être envoyé." }); }
        finally { setSending(false); }
    };

    return <div className="pcPage eap-container"><div className="eap-wrap">
        <header className="pcHeader eap-header"><div><h1>Export des arrêtés</h1><p>Générez un fichier consolidé des arrêtés de paie pour l'ensemble des collaborateurs.</p></div><div className="pcWizStep__introIcon"><FileText size={20} /></div></header>
        <div className="pcCard eap-card"><div className="pcBody eap-body">
            <section className="eap-schedule-section"><h2 className="pcTitle eap-section-title"><Mail size={16} />Préparation de l'envoi</h2>
                <div className="eap-grid eap-schedule-grid">
                    <div className="pcWizField"><label>Emails des destinataires</label><div className="eap-input-with-icon"><Mail size={14} className="eap-field-icon" /><input className="pcInput eap-icon-input" placeholder="destinataire@entreprise.com" value={recipientInput} onChange={e => setRecipientInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addRecipients())} /></div><div className="eap-recipients">{recipients.map(email => <span className="eap-recipient" key={email}>{email}<button onClick={() => setRecipients(prev => prev.filter(item => item !== email))}><X size={12} /></button></span>)}<button className="eap-add-recipient" type="button" onClick={addRecipients}>Ajouter</button></div></div>
                    <div className="pcWizField"><label>Jour de l'envoi mensuel</label><div className="eap-input-with-icon"><Calendar size={14} className="eap-field-icon" /><input type="number" min="1" max="31" className="pcInput eap-icon-input" value={payDay} onChange={e => setPayDay(e.target.value)} /></div><small className="eap-pay-period">Période exportée : {payPeriod}</small></div>
                    <div className="pcWizField" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', height: '100%', marginTop: 'auto', paddingBottom: '22px' }}>
                        <input
                            type="checkbox"
                            id="autoSend"
                            checked={autoSend}
                            onChange={e => setAutoSend(e.target.checked)}
                            style={{ width: '18px', height: '18px', accentColor: 'var(--admin-accent)', cursor: 'pointer' }}
                        />
                        <label htmlFor="autoSend" style={{ cursor: 'pointer', userSelect: 'none', fontWeight: '600', fontSize: '12px', color: '#374151' }}>
                            Activer l'envoi mensuel automatique
                        </label>
                    </div>
                </div>
                <div className="pcWizField"><label>Corps de l'email</label><textarea className="pcInput eap-email-editor" value={emailBody} onChange={e => setEmailBody(e.target.value)} /></div>
                <div className="eap-config-actions"><button type="button" className="pcBtn pcBtn--primary" onClick={saveConfiguration}>Enregistrer les informations</button></div>
            </section>
            <div className="eap-footer"><button className="pcBtn pcBtn--primary eap-download-btn" onClick={generate} disabled={generating}><Download size={18} />{generating ? "Génération..." : "Générer le fichier"}</button></div>
            {previewUrl && <div className="eap-preview"><div><Eye size={18} /><span><strong>Aperçu prêt : arrete_{fileDate}.xlsx</strong><small>Les prêts actifs affichent la mensualité à prélever pour cette période.</small></span></div><div className="eap-preview-actions"><button className="pcBtn pcBtn--primary" onClick={download}><Download size={16} />Télécharger</button><button className="pcBtn pcBtn--primary" onClick={sendEmail} disabled={sending}><Send size={16} />{sending ? "Envoi..." : "Envoyer l'email"}</button></div></div>}
        </div></div>
    </div></div>;
};
export default ExportArretePaie;
